import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { apiClient, api } from '../services/api';

const loginSchema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(6, 'Au moins 6 caractères'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    api.get('/api/config')
      .then((res) => { if (res.data?.logoUrl) setLogoUrl(res.data.logoUrl); })
      .catch(() => {});
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await login({ ...data, email: data.email.trim() });
      // Use full page redirect to ensure cookies are recognized
      window.location.href = '/';
    } catch (err) {
      const message = apiClient.getErrorMessage(err);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(135deg, hsl(30 36% 89%) 0%, hsl(30 36% 95%) 100%)' }}>
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Univers Massages"
              className="mx-auto h-36 w-auto mb-6 rounded-2xl"
            />
          )}
          <p className="text-sm" style={{ color: 'hsl(17 47% 58%)' }}>
            Espace d'administration
          </p>
        </div>

        <div className="mt-6 sm:mt-8 bg-white/80 backdrop-blur-sm px-4 sm:px-8 py-6 sm:py-10 shadow-sm rounded-lg" style={{ borderTop: '3px solid hsl(17 47% 68%)' }}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="Email"
                maxLength={255}
                {...register('email')}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                maxLength={128}
                {...register('password')}
                className={errors.password ? 'border-red-500' : ''}
              />
              {errors.password && (
                <p className="text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full text-white"
              style={{ background: 'linear-gradient(135deg, hsl(17 47% 68%) 0%, hsl(17 47% 58%) 100%)' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Connexion...' : 'Se connecter'}
            </Button>

            <Link
              to="/forgot-password"
              className="block text-center text-sm font-medium hover:underline"
              style={{ color: 'hsl(17 47% 48%)' }}
            >
              Mot de passe oublié ?
            </Link>
          </form>

        </div>
      </div>
    </div>
  );
}
